import './index.less';
import {
  Box,
  Button,
  DatePicker,
  Flex,
  Grid,
  Icon,
  InputAdornment,
  InputControl,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Space,
  Text,
  TonicProps,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  AngleRightIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ClockIcon,
} from '@tonic-one/react-icons';
import { format } from 'date-fns';
import { de, enUS, es, it, ja, pt } from 'date-fns/locale';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUtcOffsetStore } from 'src/models/useUtcOffsetModel';
import { QuarantineLogTypeEnum, TimeRangeInterface } from 'src/types/log';

import { getTimeRange, LogTimeUnit } from '../shared';
import { TimeRangeLabel } from '../../../components/Common/TimeRangeLabel';
import SmartTooltip from '@Report/SmartTooltip';
import { LogTypeEnum } from '@Types/log';

dayjs.extend(utc);

const CUSTOM_TIME_RANGE_HISTORY_LIMIT_DAYS = 180;
const CUSTOM_TIME_RANGE_SLIDING_WINDOW_DAYS = 60;

interface CustomDate {
  startDate: Date;
  startTime: string;
  endDate: Date;
  endTime: string;
}

const padTo2Digits = (num: number) => {
  return num.toString().padStart(2, '0');
};

const formatDate = (date: Date) => {
  return [
    date.getFullYear(),
    padTo2Digits(date.getMonth() + 1),
    padTo2Digits(date.getDate()),
  ].join('-');
};

const parseSavedCustomDateTime = (timestamp: string, offset: string) => {
  const zonedDateTime = dayjs.utc(timestamp).utcOffset(offset);

  return {
    date: new Date(
      zonedDateTime.year(),
      zonedDateTime.month(),
      zonedDateTime.date()
    ),
    time: zonedDateTime.format('HH:mm:ss'),
  };
};

const toPickerDate = (value: dayjs.Dayjs | Date) => {
  const date = dayjs(value);
  return new Date(date.year(), date.month(), date.date());
};

const DateInput = React.forwardRef((props, ref) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <InputControl
      ref={ref}
      startAdornment={
        <InputAdornment color={colorStyle.color.secondary}>
          <Icon as={CalendarIcon} />
        </InputAdornment>
      }
      {...props}
    />
  );
});

const CustomDateInput = (props: {
  inputFormat: string;
  isInvalid: boolean;
  defaultValue: Date;
  onChange: any;
  minDate?: Date;
  maxDate?: Date;
}) => {
  const {
    inputFormat,
    defaultValue,
    onChange,
    minDate: minDateProp,
    maxDate: maxDateProp,
  } = props;
  const [value, setValue] = useState(defaultValue);
  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  const { i18n } = useTranslation();
  const lang = useMemo(() => {
    return { en: enUS, ja, it, de, pt, es }[i18n.language] || enUS;
  }, [i18n.language]);
  const { offset } = useUtcOffsetStore();
  const today = toPickerDate(dayjs().utcOffset(offset).startOf('day'));
  const minDate = minDateProp
    ? dayjs(minDateProp)
    : dayjs(today).subtract(CUSTOM_TIME_RANGE_HISTORY_LIMIT_DAYS, 'day');
  const maxDate = maxDateProp ? dayjs(maxDateProp) : dayjs(today);
  const handleChange = (value: Date) => {
    setValue(value);
    onChange(value);
  };

  return (
    <DatePicker
      formatDate={(date: Date | number, inputFormat: string) => {
        return format(date, inputFormat, { locale: lang });
      }}
      value={value}
      onChange={handleChange}
      inputFormat={inputFormat}
      minDate={minDate.toDate()}
      maxDate={maxDate.toDate()}
      renderInput={({ error, inputProps }: any) => {
        const _error = !!inputProps.value && error;

        return (
          <DateInput
            {...inputProps}
            error={_error}
            placeholder={inputFormat}
            borderTopRightRadius={0}
            borderBottomRightRadius={0}
            width={140}
          />
        );
      }}
    />
  );
};

const CustomTimeInput = (props: {
  isInvalid: boolean;
  defaultValue: string;
  onChange: any;
}) => {
  const { isInvalid, defaultValue, onChange } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const handleTimeInputChange = (event: any) => {
    const value = event.target.value;
    onChange(value);
  };

  return (
    <InputControl
      onChange={handleTimeInputChange}
      width={110}
      borderTopLeftRadius={0}
      borderBottomLeftRadius={0}
      startAdornment={
        <InputAdornment color={colorStyle.color.secondary}>
          <Icon as={ClockIcon} />
        </InputAdornment>
      }
      error={isInvalid}
      value={defaultValue}
    />
  );
};

const DateTimePicker = (props: {
  timeRange: TimeRangeInterface;
  onApply: any;
  onClose: any;
}) => {
  const { timeRange, onApply, onClose } = props;
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { offset } = useUtcOffsetStore();
  const { t } = useTranslation('log');
  const today = useMemo(() => {
    return toPickerDate(dayjs().utcOffset(offset).startOf('day'));
  }, [offset]);
  const todayDate = useMemo(() => {
    return dayjs(today).startOf('day');
  }, [today]);
  const isCustomRange = timeRange.predefined === LogTimeUnit.Custom;
  const slidingWindowDays = CUSTOM_TIME_RANGE_SLIDING_WINDOW_DAYS;
  const historyLimitDays = CUSTOM_TIME_RANGE_HISTORY_LIMIT_DAYS;
  const maxDateSpanDays = slidingWindowDays - 1;
  const inputFormat = 'yyyy-MM-dd';
  const [dateTime, setDateTime] = useState<CustomDate>(
    isCustomRange
      ? () => {
          const start = parseSavedCustomDateTime(timeRange.start, offset);
          const end = parseSavedCustomDateTime(timeRange.end, offset);
          return {
            startDate: start.date,
            startTime: start.time,
            endDate: end.date,
            endTime: end.time,
          };
        }
      : {
          startDate: today,
          startTime: '00:00:00',
          endDate: today,
          endTime: '23:59:59',
        }
  );
  const [hasEditedStart, setHasEditedStart] = useState(isCustomRange);
  const [hasEditedEnd, setHasEditedEnd] = useState(isCustomRange);

  const [isInvalid, setIsInvalid] = React.useState(false);
  const isValidDate = (value: Date) => {
    if (value instanceof Date) {
      return !!value.getTime();
    }
    // The date format is "yyyy-MM-dd"
    const pattern = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
    return !!String(value).match(pattern);
  };
  const isValidTime = (value: string) => {
    // The time format is "HH:mm:ss"
    const pattern = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]:[0-5][0-9]$/;
    return !!String(value).match(pattern);
  };
  const onStartDateChange = (value: Date) => {
    setHasEditedStart(true);
    setDateTime({
      ...dateTime,
      startDate: value,
    });
  };
  const onEndDateChange = (value: Date) => {
    setHasEditedEnd(true);
    setDateTime({
      ...dateTime,
      endDate: value,
    });
  };
  const onStartTimeChange = (value: string) => {
    setDateTime({
      ...dateTime,
      startTime: value,
    });
  };
  const onEndTimeChange = (value: string) => {
    setDateTime({
      ...dateTime,
      endTime: value,
    });
  };
  const onApplyCustomDate = () => {
    onApply(dateTime);
  };

  const absoluteMinDate = useMemo(() => {
    return dayjs(today).startOf('day').subtract(historyLimitDays, 'day');
  }, [historyLimitDays, today]);

  const endDateMin = useMemo(() => {
    if (!hasEditedStart) {
      return absoluteMinDate.toDate();
    }

    return dayjs(dateTime.startDate).startOf('day').toDate();
  }, [absoluteMinDate, dateTime.startDate, hasEditedStart]);

  const endDateMax = useMemo(() => {
    if (!hasEditedStart) {
      return todayDate.toDate();
    }

    // Use slidingWindowDays - 1 so that start 00:00:00 to end 23:59:59
    // covers exactly slidingWindowDays days (e.g. Nov 1 to Dec 30 = 60 days)
    const startPlusWindow = dayjs(dateTime.startDate)
      .startOf('day')
      .add(maxDateSpanDays, 'day');
    return (
      startPlusWindow.isBefore(todayDate) ? startPlusWindow : todayDate
    ).toDate();
  }, [dateTime.startDate, hasEditedStart, maxDateSpanDays, todayDate]);

  const startDateMin = useMemo(() => {
    if (!hasEditedEnd) {
      return absoluteMinDate.toDate();
    }

    const endMinusWindow = dayjs(dateTime.endDate)
      .startOf('day')
      .subtract(maxDateSpanDays, 'day');
    return (
      endMinusWindow.isAfter(absoluteMinDate) ? endMinusWindow : absoluteMinDate
    ).toDate();
  }, [absoluteMinDate, dateTime.endDate, hasEditedEnd, maxDateSpanDays]);

  const startDateMax = useMemo(() => {
    if (!hasEditedEnd) {
      return todayDate.toDate();
    }

    const currentEnd = dayjs(dateTime.endDate).startOf('day');
    return (currentEnd.isBefore(todayDate) ? currentEnd : todayDate).toDate();
  }, [dateTime.endDate, hasEditedEnd, todayDate]);

  useEffect(() => {
    if (
      !(
        isValidDate(dateTime.startDate) &&
        isValidTime(dateTime.startTime) &&
        isValidDate(dateTime.endDate) &&
        isValidTime(dateTime.endTime)
      )
    ) {
      setIsInvalid(true);
      return;
    }

    const startMs = Date.parse(
      `${formatDate(dateTime.startDate)} ${dateTime.startTime}`
    );
    const endMs = Date.parse(
      `${formatDate(dateTime.endDate)} ${dateTime.endTime}`
    );

    // Keep the current selection and let the user correct invalid ranges manually.
    if (endMs < startMs) {
      setIsInvalid(true);
      return;
    }

    // check if the date span exceeds the sliding window (date level, ignoring time)
    // Max allowed span is slidingWindowDays - 1 calendar days so that
    // start 00:00:00 to end 23:59:59 covers exactly slidingWindowDays days
    const spanDays = dayjs(dateTime.endDate).diff(
      dayjs(dateTime.startDate),
      'day'
    );
    if (spanDays > maxDateSpanDays) {
      setIsInvalid(true);
      return;
    }

    setIsInvalid(false);
  }, [dateTime, maxDateSpanDays]);

  return (
    <Box
      backgroundColor={colorStyle.background.tertiary}
      position="absolute"
      right={0}
      top={0}
      px="3x"
      py="3x"
      borderLeft={1}
      borderColor={colorStyle.divider}
    >
      <Flex alignItems="center" columnGap="3x" mb="6x">
        <Button variant="ghost" onClick={onClose}>
          <Icon as={ChevronLeftIcon} color={colorStyle.color.secondary} />
        </Button>
        <Text fontSize="md" lineHeight="md">
          {t('log.timeRange.customPeriod')}
        </Text>
      </Flex>
      <Grid templateColumns="auto 1fr" alignItems="center" rowGap="3x" mb="3x">
        <Text minWidth="16x">{t('log.timeRange.from')}:</Text>
        <Flex>
          <CustomDateInput
            inputFormat={inputFormat}
            isInvalid={!isValidDate(dateTime.startDate)}
            defaultValue={dateTime.startDate}
            onChange={onStartDateChange}
            minDate={startDateMin}
            maxDate={startDateMax}
          />
          <CustomTimeInput
            isInvalid={!isValidTime(dateTime.startTime)}
            defaultValue={dateTime.startTime}
            onChange={onStartTimeChange}
          />
        </Flex>
        <Text minWidth="16x">{t('log.timeRange.to')}:</Text>
        <Flex>
          <CustomDateInput
            inputFormat={inputFormat}
            isInvalid={!isValidDate(dateTime.endDate)}
            defaultValue={dateTime.endDate}
            onChange={onEndDateChange}
            minDate={endDateMin}
            maxDate={endDateMax}
          />
          <CustomTimeInput
            isInvalid={!isValidTime(dateTime.endTime)}
            defaultValue={dateTime.endTime}
            onChange={onEndTimeChange}
          />
        </Flex>
      </Grid>
      <Flex justifyContent="flex-end">
        <Grid templateColumns="1fr 1fr" columnGap="2x">
          <Button
            variant="primary"
            disabled={isInvalid}
            onClick={onApplyCustomDate}
          >
            {t('log.button.apply')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('log.button.cancel')}
          </Button>
        </Grid>
      </Flex>
    </Box>
  );
};

const TimeRange = ({
  type,
  timeRange,
  setTimeRange,
  showLabel = true,
  placeholder,
  width = '192px',
}: {
  type: LogTypeEnum | QuarantineLogTypeEnum;
  timeRange: TimeRangeInterface;
  setTimeRange: (v: TimeRangeInterface) => void;
  showLabel?: boolean;
  placeholder?: string;
  width?: TonicProps['width'];
}) => {
  const [isVisible, setVisible] = useState<boolean>(false);
  const { offset } = useUtcOffsetStore();
  const { t } = useTranslation();

  const handleMenuItemClick = (event: any) => {
    const value = event.currentTarget.getAttribute('value') as LogTimeUnit;
    if (value === LogTimeUnit.Custom) {
      event.preventDefault();
      if (!isVisible) {
        setVisible(true);
      }
      return;
    }
    setVisible(false);
    const _timeRange = getTimeRange(value, type);
    setTimeRange(_timeRange);
  };

  const labelMap = useMemo(() => {
    return {
      all: t('log.timeRange.all'),
      '24h': t('log.timeRange.last24h'),
      '1w': t('log.timeRange.last7D'),
      '1M': t('log.timeRange.last30D'),
      custom: t('log.timeRange.customPeriod'),
    };
  }, [t]);

  // calc time range by predefined every render
  let start = timeRange.start;
  let end = timeRange.end;
  if (timeRange.predefined !== LogTimeUnit.Custom) {
    const newRange = getTimeRange(timeRange.predefined, type);
    start = newRange.start;
    end = newRange.end;
  }

  //Note: the backend time range, if predefined !== custom, backend calc the range by predefined
  //      otherwise, predefined === custom, backend use the start and end in request body
  return (
    <Flex alignItems="center">
      {showLabel && (
        <>
          <TimeRangeLabel start={start} end={end} />
          <Space width="3x" />
        </>
      )}
      <Menu
        onClose={() => {
          if (isVisible) {
            setVisible(false);
          }
        }}
      >
        {({ onClose, onOpen }: any) => (
          <>
            <MenuButton
              variant="secondary"
              size="md"
              width={width}
              height="32px"
              className="time-range-button"
            >
              <Flex>
                {placeholder && <Text opacity="0.6">{placeholder}:&nbsp;</Text>}
                <SmartTooltip label={labelMap[timeRange.predefined]}>
                  {labelMap[timeRange.predefined]}
                </SmartTooltip>
              </Flex>
            </MenuButton>
            <MenuList width={width}>
              {isVisible ? (
                <DateTimePicker
                  timeRange={timeRange}
                  onApply={(value: CustomDate) => {
                    onClose();
                    setVisible(false);
                    const startTime = dayjs(
                      `${formatDate(value.startDate)}T${
                        value.startTime
                      }${offset}`
                    );
                    const endTime = dayjs(
                      `${formatDate(value.endDate)}T${value.endTime}${offset}`
                    );
                    setTimeRange({
                      predefined: LogTimeUnit.Custom,
                      start: startTime.toISOString(),
                      end: endTime.toISOString(),
                      timezoneOffset: offset,
                    });
                  }}
                  onClose={() => {
                    onOpen();
                    setVisible(false);
                  }}
                />
              ) : (
                <>
                  {[
                    LogTimeUnit.Last24h,
                    LogTimeUnit.Last7d,
                    LogTimeUnit.Last30d,
                    LogTimeUnit.Custom,
                  ].map((period) => (
                    <MenuItem
                      key={period}
                      value={period}
                      onClick={handleMenuItemClick}
                      lineHeight="20px"
                    >
                      {labelMap[period]}
                      {period === LogTimeUnit.Custom && (
                        <Icon as={AngleRightIcon} ml={15} />
                      )}
                    </MenuItem>
                  ))}
                </>
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </Flex>
  );
};
export default React.memo(TimeRange);
